import { useCallback, useEffect, useState } from 'react';
import { UNGROUPED_ID } from './groupByCampaign';

const STORAGE_KEY = 'taxonomy-generation:campaign-rows';

type ExpansionState = Record<string, boolean>;

/**
 * Reads the stored state, tolerating every way this can fail.
 *
 * `localStorage` throws outright in a private window or with site data blocked,
 * and otherwise returns whatever was last written — which may be from an older
 * shape. A list page must render either way, so anything unreadable is treated
 * as "nothing recorded" rather than allowed to escape.
 */
function read(): ExpansionState {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).filter(
        (entry): entry is [string, boolean] => typeof entry[1] === 'boolean',
      ),
    );
  } catch {
    return {};
  }
}

function write(state: ExpansionState): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // A viewer who cannot persist still gets working expand/collapse for this
    // session; losing it on reload is not worth failing the page over.
  }
}

/**
 * Whether a group nobody has touched yet starts open.
 *
 * Campaigns start CLOSED, as asked: a campaign is a deliberate grouping, and
 * collapsing them is what makes the grouping visible at all.
 *
 * Ungrouped starts OPEN, and that difference is load-bearing rather than a
 * nicety. Every taxonomy that predates campaigns has `campaignId: null`, so on
 * the first load after this ships a workspace has one group holding everything
 * and no campaigns at all — and a closed Ungrouped would render that as a page
 * with a single grey row and no taxonomies anywhere, on a screen that has always
 * listed them. It is a remainder, not a grouping someone chose, so there is
 * nothing for closing it by default to reveal.
 */
function defaultFor(id: string): boolean {
  return id === UNGROUPED_ID;
}

/**
 * Nothing overridden. A module-scope constant so the identity is stable across
 * renders and `isExpanded`'s dependency list does not change on every pass.
 */
const NO_OVERRIDES: ExpansionState = {};

/**
 * Which campaign rows are open.
 *
 * The single owner of that question. Expansion has three inputs and they are
 * ranked here rather than split across files: an override the user set against
 * the CURRENT search term, then whether a search is running at all, then what
 * was persisted, then the default above.
 *
 * A running search expands every group it renders — the search has already
 * narrowed the set, and a hit inside a closed row is a hit nobody can see. That
 * applies to a campaign matched by its own NAME too, which used to be the one
 * case that stayed shut over the taxonomies the grouping had deliberately kept.
 *
 * Overrides are per-term and in memory, so an explicit collapse wins for as long
 * as that search is on screen without teaching the persisted state that this
 * campaign should stay shut the next time a search happens to match it. A
 * session recorded against an older term is simply not read, so there is nothing
 * to reset and no render-phase state update to schedule.
 *
 * The persisted half stores only rows the user has actually toggled, so a
 * campaign created after the last write still gets the default rather than
 * inheriting whatever an older entry happened to say. Deliberately not pruned
 * when a campaign is deleted: the record is tiny, a stale id matches nothing,
 * and pruning would mean this page owning a lifecycle it does not otherwise care
 * about.
 */
export function useExpandedCampaigns(term: string) {
  const [stored, setStored] = useState<ExpansionState>(read);
  const [session, setSession] = useState<{ term: string; overrides: ExpansionState }>({
    term,
    overrides: NO_OVERRIDES,
  });

  const searching = term !== '';

  /*
   * CLEARED when the term changes, not merely shadowed. Shadowing alone leaves
   * the old overrides sitting there, and typing the same term a second time
   * revives them — so a running search would render a matching group collapsed,
   * which is the screen this hook exists to prevent. React's documented
   * "adjust state during render": it re-renders before committing anything, and
   * the guard is what makes it terminate.
   */
  if (session.term !== term) {
    setSession({ term, overrides: NO_OVERRIDES });
  }
  const overrides = session.term === term ? session.overrides : NO_OVERRIDES;

  const isExpanded = useCallback(
    (id: string) => overrides[id] ?? (searching || (stored[id] ?? defaultFor(id))),
    [overrides, searching, stored],
  );

  /**
   * Takes the state that was on SCREEN, not the stored one.
   *
   * They differ whenever a search is expanding a group whose stored state is
   * collapsed. Flipping the stored value there wrote back the state already
   * being displayed, so the click changed nothing and left the persisted
   * preference inverted once the search was cleared.
   */
  const toggle = useCallback(
    (id: string, currentlyExpanded: boolean) => {
      const next = !currentlyExpanded;
      /*
       * A collapse made to quiet a search is about THAT search, and is gone when
       * it is. Persisting it would let one click while searching permanently
       * close Ungrouped — precisely the page `defaultFor` above exists to keep
       * nobody from landing on.
       */
      if (searching) {
        setSession((prev) => ({ term, overrides: { ...prev.overrides, [id]: next } }));
        return;
      }
      setStored((prev) => ({ ...prev, [id]: next }));
    },
    [searching, term],
  );

  /*
   * Persisted here rather than inside the updater above: a state updater has to
   * be pure, and `write` touches localStorage.
   */
  useEffect(() => {
    write(stored);
  }, [stored]);

  return { isExpanded, toggle };
}
